import React, { useEffect, useState } from "react";
import Wordmark from "../components/Wordmark.jsx";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth.jsx";

/**
 * Invite entry — where an invited person joins.
 *
 * The audience is a grandparent, an aunt, a long-time family friend: someone who
 * was asked personally and may never have used anything like this. So the screen
 * leads with the human fact — who asked, about whom, and why — and only then
 * shows two fields. The email is fixed by the invitation and shown read-only, so
 * there is nothing to get wrong.
 *
 * Unlike a share link, expired and already-used invitations say WHICH they are.
 * This person was named by the inviter, so "you already accepted this, try
 * signing in" is far more use to them than a blanket refusal.
 */
export default function InviteEntry() {
  const { token }   = useParams();
  const navigate    = useNavigate();
  const { refresh } = useAuth();

  const [info, setInfo]   = useState(null);
  const [error, setError] = useState(null);
  const [fatal, setFatal] = useState(null);
  const [busy, setBusy]   = useState(false);
  const [form, setForm]   = useState({ full_name: "", password: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/invitations/${token}/check`);
        const d = await r.json();
        if (cancelled) return;
        if (!r.ok) { setFatal(typeof d.error === "string" ? d.error : "This invitation isn't valid."); return; }
        setInfo(d);
        if (d.name) setForm(f => ({ ...f, full_name: d.name }));
      } catch {
        if (!cancelled) setFatal("We couldn't check that invitation.");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  async function accept(e) {
    e.preventDefault();
    setError(null);
    if (!form.full_name.trim()) { setError("Please enter your name."); return; }
    if (form.password.length < 8) { setError("Please choose a password of at least 8 characters."); return; }
    setBusy(true);
    try {
      const r = await fetch(`/api/invitations/${token}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ full_name: form.full_name.trim(), password: form.password }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.error === "string" ? d.error : "Couldn't accept the invitation.");
      await refresh();
      navigate(d.redirect, { replace: true });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  if (fatal) {
    return (
      <Frame>
        <h1 style={s.heading}>This invitation isn't available</h1>
        <p style={s.body}>{fatal}</p>
        <Link to="/login" style={s.link}>Go to sign in</Link>
      </Frame>
    );
  }

  if (!info) return <Frame><p style={s.body}>Just a moment…</p></Frame>;

  const who  = info.invited_by || "Someone";
  const name = info.client?.display_name;

  return (
    <Frame>
      <h1 style={s.heading}>
        {info.interview
          ? `${who} would like to ask you a few questions about ${name || "someone they support"}`
          : `${who} invited you${name ? ` to help with ${name}'s profile` : ""}`}
      </h1>
      {info.interview?.note && <blockquote style={s.quote}>“{info.interview.note}”</blockquote>}
      {info.interview && (
        <p style={s.body}>About ten minutes, whenever suits you. Set a name and password below and the questions are waiting.</p>
      )}

      {info.message && <blockquote style={s.quote}>“{info.message}”</blockquote>}

      <p style={s.body}>
        {info.role_description}
        {name ? ` Everything you add helps the people caring for ${name} know them better.` : ""}
      </p>

      <form onSubmit={accept} style={s.form}>
        <label style={s.label} htmlFor="email">Your email</label>
        <input id="email" style={{ ...s.input, ...s.readonly }} value={info.email} readOnly />

        <label style={s.label} htmlFor="full_name">Your name</label>
        <input id="full_name" style={s.input} value={form.full_name}
               onChange={e => set("full_name", e.target.value)}
               autoComplete="name" disabled={busy} autoFocus />

        <label style={s.label} htmlFor="password">Choose a password</label>
        <input id="password" type="password" style={s.input} value={form.password}
               onChange={e => set("password", e.target.value)}
               autoComplete="new-password" disabled={busy} />
        <p style={s.hint}>At least 8 characters.</p>

        {error && <p style={s.error}>{error}</p>}

        <button type="submit" style={{ ...s.btn, ...(busy ? s.btnBusy : {}) }} disabled={busy}>
          {busy ? "Setting things up…" : "Accept and continue"}
        </button>
      </form>
    </Frame>
  );
}

function Frame({ children }) {
  return (
    <div style={s.page}>
      <div style={s.card}>
        <Wordmark />
        {children}
      </div>
    </div>
  );
}

const s = {
  page:     { minHeight: "100dvh", background: "var(--color-bg-subtle)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
  card:     { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-xl)", padding: "40px 36px", width: "100%", maxWidth: 440 },
  wordmark: { marginBottom: 24, fontSize: 20, fontWeight: "var(--weight-medium)", letterSpacing: "-0.4px", color: "var(--color-text-primary)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
  dot:      { display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: "var(--color-accent)", flexShrink: 0 },
  heading:  { fontSize: "var(--text-lg)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)", marginBottom: 12, lineHeight: "1.35" },
  body:     { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)", marginBottom: 20 },
  quote:    { margin: "0 0 16px", padding: "10px 14px", borderLeft: "3px solid var(--color-accent)", background: "var(--color-bg-subtle)", fontSize: "var(--text-sm)", color: "var(--color-text-primary)", fontStyle: "italic", lineHeight: "var(--leading-normal)" },
  form:     { display: "flex", flexDirection: "column" },
  label:    { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginBottom: 6 },
  input:    { width: "100%", padding: "10px 12px", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", fontSize: "var(--text-base)", background: "var(--color-bg-subtle)", color: "var(--color-text-primary)", fontFamily: "inherit", marginBottom: 16, outline: "none", boxSizing: "border-box" },
  readonly: { color: "var(--color-text-tertiary)", cursor: "default" },
  hint:     { fontSize: 11, color: "var(--color-text-tertiary)", marginTop: -12, marginBottom: 16 },
  error:    { fontSize: "var(--text-sm)", color: "var(--color-critical)", background: "var(--color-critical-bg)", borderRadius: "var(--radius-sm)", padding: "8px 12px", marginBottom: 12 },
  btn:      { width: "100%", padding: "11px 16px", background: "var(--color-accent)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", fontSize: "var(--text-base)", fontWeight: "var(--weight-semibold)", cursor: "pointer" },
  btnBusy:  { opacity: 0.6, cursor: "not-allowed" },
  link:     { color: "var(--color-accent)", fontSize: "var(--text-sm)" },
};
